import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, FileText, Upload, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, fmtEur, openFile } from "../../lib/api";
import { useSeo } from "../../lib/seo";
import { QUOTE, BAT_VERSION, SUPPLY, TECHNIQUES } from "../../lib/labels";
import { useClient } from "./Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

function Tracking({ d }) {
  const t = d.tracking;
  if (!t) return null;
  const color = t.late ? "#C25E38" : t.state === "livre" ? "#2F7D5B" : t.state === "attente_client" ? "#C25E38" : "#1C1917";
  return (
    <section className="k-card p-6" data-testid="client-tracking">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="k-eyebrow text-[#57534E]">Où en est ma commande</p>
          <h2 className="font-display text-2xl mt-1" data-testid="tracking-headline">{t.late ? "Retard sur la date annoncée" : t.headline}</h2>
        </div>
        <span className="font-mono-k text-sm" data-testid="tracking-percent">{t.percent}%</span>
      </div>
      <div className="mt-4 h-2 bg-[#E8E2D8] overflow-hidden" role="progressbar" aria-valuenow={t.percent} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full transition-[width] duration-700" style={{ width: `${t.percent}%`, backgroundColor: color }} />
      </div>
      <p className="text-sm text-[#57534E] mt-3" data-testid="tracking-detail">{t.detail}</p>
      {d.delivery_due && (
        <p className={`text-sm mt-2 ${t.late ? "text-[#C25E38]" : ""}`} data-testid="tracking-due">
          {t.state === "livre" ? "Remise effectuée" : t.late ? `Remise prévue le ${fmtDate(d.delivery_due)} — dépassée` : `Remise prévue le ${fmtDate(d.delivery_due)}`}
        </p>
      )}
      {d.delivery_note && <p className="text-sm mt-3 border-l-2 border-[#1C1917] pl-3 whitespace-pre-line" data-testid="tracking-note">{d.delivery_note}</p>}
      {t.state === "attente_client" && !t.late && <p className="text-xs text-[#C25E38] mt-3">Une action de votre part est attendue.</p>}
      <p className="text-xs text-[#8C857B] mt-3">Dernière mise à jour : {fmtDateTime(d.updated_at || d.created_at)}</p>
    </section>
  );
}

function Steps({ steps }) {
  return (
    <ol className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="client-steps">
      {steps.map((s, i) => (
        <li key={s.label} className={`border-t-2 pt-3 text-sm ${s.done ? "border-[#2F7D5B]" : s.attention ? "border-[#C25E38]" : s.current ? "border-[#1C1917]" : "border-[#E8E2D8] text-[#8C857B]"}`}>
          <span className="font-mono-k text-[10px]">0{i + 1}</span>
          <p className="flex items-center gap-1">{s.done && <Check size={14} className="text-[#2F7D5B]" />}{s.attention ? "Complément demandé" : s.label}</p>
        </li>
      ))}
    </ol>
  );
}

function Bats({ bats, reload }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  if (!bats.length) return null;
  const decide = async (b, decision) => {
    setBusy(true);
    try { await api.post(`/client/bats/${b.id}/decision`, { decision, comment }); toast.success(decision === "valide" ? "BAT validé, merci." : "Corrections transmises à l'atelier."); setComment(""); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="k-card p-6" data-testid="client-bats">
      <h2 className="k-h3">Bon à tirer (BAT)</h2>
      <p className="text-sm text-[#57534E] mt-1">Vérifiez emplacement, taille et couleurs. Aucune production ne démarre sans votre validation.</p>
      <ul className="mt-4 divide-y divide-[#E8E2D8]">
        {bats.map((b) => (
          <li key={b.id} className="py-4" data-testid={`client-bat-v${b.version}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button onClick={() => openFile(`/client/files/${b.file_id}`)} className="k-link text-sm flex items-center gap-2" data-testid={`client-bat-open-v${b.version}`}><FileText size={16} /> Version {b.version} — {b.file_name}</button>
              <span className="k-badge">{BAT_VERSION[b.status]}</span>
            </div>
            {b.note && <p className="text-sm text-[#57534E] mt-2">{b.note}</p>}
            {b.client_comment && <p className="text-sm mt-2 border-l-2 border-[#C25E38] pl-3">Vos corrections : {b.client_comment}</p>}
            {b.status === "envoye" && (
              <div className="mt-4 grid gap-3">
                <textarea className="k-input" rows={2} placeholder="Corrections souhaitées (obligatoire pour demander une correction)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="bat-comment" />
                <div className="flex flex-wrap gap-3">
                  <button className="k-btn k-btn-sm" disabled={busy} onClick={() => decide(b, "valide")} data-testid="bat-validate-btn">Je valide cette version</button>
                  <button className="k-btn-outline k-btn-sm" disabled={busy || !comment.trim()} onClick={() => decide(b, "corrections")} data-testid="bat-corrections-btn">Demander une correction</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Reorder({ d, onDone }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState({});
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await api.post(`/client/requests/${d.id}/reorder`, { quantities: q, comment }); toast.success(`Réassort ${r.data.ref} demandé.`); setOpen(false); onDone(r.data.id); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button className="k-btn-outline k-btn-sm" onClick={() => setOpen(true)} data-testid="reorder-open"><RotateCcw size={14} /> Demander un réassort</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-[#FBF9F5]" data-testid="reorder-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl font-medium">Réassort de {d.ref}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#57534E]">Nous reprenons vos produits et logos. Prix, disponibilités et délais seront revérifiés dans un nouveau devis.</p>
          {d.items.map((it) => (
            <label key={it.id} className="flex items-center justify-between gap-3 text-sm">
              <span>{it.category} {it.model} (précédemment {it.quantity || "?"})</span>
              <input type="number" min="1" className="k-input w-24" placeholder={it.quantity || ""} value={q[it.id] || ""} onChange={(e) => setQ({ ...q, [it.id]: e.target.value })} data-testid={`reorder-qty-${it.id}`} />
            </label>
          ))}
          <textarea className="k-input" rows={3} placeholder="Date souhaitée, changements de tailles ou de couleurs…" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="reorder-comment" />
          <button className="k-btn" disabled={busy} onClick={send} data-testid="reorder-submit">Envoyer la demande de réassort</button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ClientFiles({ id, files, reload }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/client/requests/${id}/files`, fd); toast.success("Fichier ajouté"); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };
  return (
    <section className="k-card p-6" data-testid="client-files">
      <h2 className="k-h3">Documents & fichiers</h2>
      <ul className="mt-3 text-sm divide-y divide-[#E8E2D8]">
        {files.map((f) => <li key={f.id} className="py-2 flex justify-between gap-2"><button className="k-link truncate text-left" onClick={() => openFile(`/client/files/${f.id}`)}>{f.name}</button><span className="text-[#8C857B] shrink-0">{f.uploaded_by === "client" ? "vous" : "atelier"}</span></li>)}
        {!files.length && <li className="py-2 text-[#8C857B]">Aucun fichier.</li>}
      </ul>
      <label className="k-btn-outline k-btn-sm mt-4 cursor-pointer" data-testid="client-upload-label">
        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Ajouter un fichier
        <input type="file" className="sr-only" accept=".png,.jpg,.jpeg,.webp,.heic,.pdf,.svg,.ai,.eps" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="client-upload-input" />
      </label>
    </section>
  );
}

function ClientMessages({ id, messages, reload }) {
  const [msg, setMsg] = useState("");
  const send = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/client/requests/${id}/messages`, { body: msg }); setMsg(""); toast.success("Message envoyé à l'atelier"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="k-card p-6" data-testid="client-messages">
      <h2 className="k-h3">Échanges</h2>
      <div className="mt-3 grid gap-3 max-h-96 overflow-auto">
        {messages.map((m) => (
          <div key={m.id} className={`p-3 text-sm ${m.author_type === "client" ? "bg-[#F4F0EA] ml-6" : "bg-white border border-[#E8E2D8] mr-6"}`}>
            <p className="text-xs text-[#8C857B]">{m.author_type === "client" ? "Vous" : "Atelier KOUOH"} · {fmtDateTime(m.at)}</p>
            <p className="whitespace-pre-line mt-1">{m.body}</p>
          </div>
        ))}
        {!messages.length && <p className="text-sm text-[#8C857B]">Aucun message pour le moment.</p>}
      </div>
      <textarea className="k-input mt-4" rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Votre message à l'atelier" data-testid="client-message-input" />
      <button className="k-btn k-btn-sm mt-3" onClick={send} data-testid="client-message-send">Envoyer</button>
    </section>
  );
}

export default function ClientDossier() {
  useSeo({ title: "Mon dossier", path: "/espace-client/dossiers", noindex: true });
  const { id } = useParams();
  const { me } = useClient();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/client/requests/${id}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e))), [id]);
  useEffect(() => { if (me) load(); }, [me, load]);
  if (err) return <section className="k-section"><p role="alert">{err}</p><Link to="/espace-client/dossiers" className="k-link">Retour</Link></section>;
  if (!d) return null;
  const lastStaff = [...d.messages].reverse().find((m) => m.author_type === "staff");
  return (
    <section className="k-section pt-10" data-testid="client-dossier">
      <Link to="/espace-client/dossiers" className="text-sm flex items-center gap-2 hover:text-[#C25E38]" data-testid="client-back"><ArrowLeft size={14} /> Mes dossiers</Link>
      <div className="mt-6 flex flex-wrap justify-between items-end gap-4">
        <div><p className="font-mono-k text-sm text-[#C25E38]" data-testid="client-dossier-ref">{d.ref}</p><h1 className="k-h1 mt-2">{d.offer_label}</h1><p className="text-sm text-[#57534E] mt-2">Demande du {fmtDate(d.created_at)}</p></div>
        <Reorder d={d} onDone={(nid) => (window.location.href = `/espace-client/dossiers/${nid}`)} />
      </div>
      <div className="mt-10"><Tracking d={d} /></div>
      <div className="mt-8"><Steps steps={d.steps} /></div>
      {d.awaiting_client && (
        <div className="mt-8 border border-[#E5A992] bg-[#FDF4EE] p-5" data-testid="client-info-request">
          <p className="font-semibold">L'atelier a besoin de quelques précisions.</p>
          {lastStaff && <p className="text-sm mt-2 whitespace-pre-line">{lastStaff.body}</p>}
          <p className="text-sm mt-2 text-[#57534E]">Répondez ci-dessous ou ajoutez un fichier.</p>
        </div>
      )}
      <div className="mt-10 grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7 grid gap-6 content-start">
          {d.quotes.length > 0 && (
            <section className="k-card p-6" data-testid="client-quotes">
              <h2 className="k-h3">Devis</h2>
              <ul className="mt-3 divide-y divide-[#E8E2D8]">
                {d.quotes.map((q) => (
                  <li key={q.id} className="py-3 flex flex-wrap justify-between gap-3 text-sm">
                    <span>Devis {q.number} · {fmtEur(q.amount_ht)} HT</span>
                    <span className="flex gap-3 items-center">
                      <span className="k-badge">{QUOTE[q.status]}{q.signed ? " · signé" : ""}</span>
                      {q.file_id && <button className="k-link" onClick={() => openFile(`/client/files/${q.file_id}`)} data-testid={`client-quote-pdf-${q.id}`}>PDF</button>}
                      {q.pdf_url?.startsWith("https://") && <a className="k-link" href={q.pdf_url} target="_blank" rel="noreferrer">PDF</a>}
                      {q.payment_link && <a className="k-link" href={q.payment_link} target="_blank" rel="noreferrer" data-testid={`client-quote-pay-${q.id}`}>Payer</a>}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="k-hint">Les devis sont émis depuis notre logiciel de facturation ; l'acceptation se fait selon les modalités indiquées sur le devis.</p>
            </section>
          )}
          <Bats bats={d.bats} reload={load} />
          <section className="k-card p-6">
            <h2 className="k-h3">Votre demande</h2>
            {d.items.map((it, i) => (
              <div key={it.id} className="mt-3 text-sm"><p className="font-semibold">{i + 1}. {it.category} {it.model} × {it.quantity || "?"}</p>
                <p className="text-[#57534E]">{SUPPLY[it.supply]} · {TECHNIQUES[it.technique] || "Conseil de l'atelier"}{it.placements?.length ? ` · ${it.placements.map((p) => p.zone).join(", ")}` : ""}</p></div>
            ))}
            {d.event && <p className="text-sm mt-3">Événement le {d.event.date} — {d.event.venue} {d.event.city} · {d.event.participants} participants</p>}
            {d.custom && <p className="text-sm mt-3 whitespace-pre-line">{d.custom.goal}</p>}
            {d.common?.desired_date && <p className="text-sm mt-3 text-[#57534E]">Date souhaitée : {d.common.desired_date}</p>}
          </section>
        </div>
        <div className="lg:col-span-5 grid gap-6 content-start">
          <ClientFiles id={id} files={d.files} reload={load} />
          <ClientMessages id={id} messages={d.messages} reload={load} />
        </div>
      </div>
    </section>
  );
}
